import { Button, ConfigProvider, Divider, Modal, Spin } from "antd";
import { QRCodeCanvas } from "qrcode.react";
import React, { useEffect, useRef, useState } from "react";
import { serverImgFullPath } from "../../utils/Common";

function QrModal({ openModal, record }) {
  const [qrValue, setQrValue] = useState("");
  const [qrImageUrl, setQrImageUrl] = useState("");
  const [isImageGenerating, setIsImageGenerating] = useState(false);
  const qrCanvasRef = useRef(null);

  useEffect(() => {
    if (openModal) {
      setQrImageUrl("");
      setIsImageGenerating(false);
      // Set QR value when modal opens
      if (record?.param?.slug) {
        const { param } = record;
        let segment = param?.isRestaurant ? "rs" : "gr";
        const qrUrl = `${process.env.REACT_APP_FRONTEND_BASE_URL}/${segment}/${param.slug}`;
        setQrValue(qrUrl);
      }
    }
  }, [openModal, record]);

  useEffect(() => {
    if (openModal && qrValue && !isImageGenerating) {
      generateQrImage();
    }
  }, [openModal, qrValue]);

  const generateQrImage = async () => {
    if (!qrCanvasRef.current || isImageGenerating) return;

    setIsImageGenerating(true);

    try {
      const qrCanvas = qrCanvasRef.current;
  
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const canvasSizeWidth = 512;
      const canvasSizeHeight = 724;
      canvas.width = canvasSizeWidth;
      canvas.height = canvasSizeHeight;

      // Wait a short moment for QR canvas to be ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Load background image
      const bgImage = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = `${process.env.PUBLIC_URL}/images/bg-qr-code.jpg`;
      });

      ctx.drawImage(bgImage, 0, 0, canvasSizeWidth, canvasSizeHeight);

      // Load logo image
      const logoImage = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = serverImgFullPath(record?.param?.logo, "/images/logo.png");
      });

      const name = record?.param?.name || "Default Name";
      ctx.font = "italic lighter 24px Georgia";
      ctx.fillStyle = "#FFFFFF";

      const maxWidth = 200;
      let lines = [];
      let currentLine = "";

      const words = name.split(" ");
      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const testWidth = ctx.measureText(testLine).width;

        if (testWidth <= maxWidth) {
          currentLine = testLine;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      });

      if (currentLine) {
        lines.push(currentLine);
      }

      const totalTextHeight = lines.length * 40;
  
      const qrSize = 200;
      const qrX = (canvasSizeWidth - qrSize) / 3;
      const qrYFromBottom = 310;
      const qrY = canvasSizeHeight - qrYFromBottom - qrSize;

      const gapBetweenTextAndQr = 25;
      const gapBetweenLogoAndText = 25;

      let textY = qrY - totalTextHeight - gapBetweenTextAndQr;

      const logoSize = 60;
      const logoX = qrX + (qrSize - logoSize) / 2;
      const logoY = textY - logoSize - gapBetweenLogoAndText;

      ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);

      textY += gapBetweenLogoAndText;

      lines.forEach((line) => {
        const lineWidth = ctx.measureText(line).width;
        const lineX = qrX + (qrSize - lineWidth) / 2;
  
        ctx.fillText(line, lineX, textY);
        textY += 40;
      });

      // Get QR code from canvas ref
      const qrDataUrl = qrCanvas.toDataURL();
      const qrImage = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = qrDataUrl;
      });

      ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

      const borderSize = 20;
      ctx.strokeStyle = "#FFD700";
      ctx.lineWidth = 5;

      ctx.beginPath();
      ctx.moveTo(qrX + borderSize, qrY - borderSize);
      ctx.lineTo(qrX - borderSize, qrY - borderSize);
      ctx.lineTo(qrX - borderSize, qrY + borderSize);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(qrX + qrSize - borderSize, qrY - borderSize);
      ctx.lineTo(qrX + qrSize + borderSize, qrY - borderSize);
      ctx.lineTo(qrX + qrSize + borderSize, qrY + borderSize);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(qrX + borderSize, qrY + qrSize + borderSize);
      ctx.lineTo(qrX - borderSize, qrY + qrSize + borderSize);
      ctx.lineTo(qrX - borderSize, qrY + qrSize - borderSize);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(qrX + qrSize - borderSize, qrY + qrSize + borderSize);
      ctx.lineTo(qrX + qrSize + borderSize, qrY + qrSize + borderSize);
      ctx.lineTo(qrX + qrSize + borderSize, qrY + qrSize - borderSize);
      ctx.stroke();

      const finalDataUrl = canvas.toDataURL("image/png");
      setQrImageUrl(finalDataUrl);
    } catch (error) {
      console.error("Error generating QR image:", error);
    } finally {
      setIsImageGenerating(false);
    }
  };

  const handleDownload = () => {
    if (qrImageUrl) {
      const link = document.createElement("a");
      link.href = qrImageUrl;
      link.download = `${record?.param?.slug || "qr-code"}.png`;
      link.click();
      record.onFinish?.();
    }
  };

  const handleCancel = () => {
    setQrValue("");
    setQrImageUrl("");
    record.onCancel?.();
  };

  const handlePrint = () => {
    if (qrImageUrl) {
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print QR Code</title>
            </head>
            <body style="display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0;">
              <img src="${qrImageUrl}" alt="QR Code for ${
          record?.param?.slug || "group"
        }" />
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        record.onFinish?.();
      }
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultHoverBorderColor: "#FFD700",
            defaultHoverColor: "#000000",
            fontFamily: "ubold",
          },
        },
      }}
    >
      <Modal
        maskClosable={false}
        open={openModal}
        footer={null}
        onCancel={handleCancel}
        width={800}
        style={{ padding: "40px" }}
        destroyOnClose={true}
      >
        <div className="flex flex-col font-uregular">
          <div className="text-2xl font-ubold">
            QR Code for {record?.param?.name}
          </div>
          <Divider />
          <div
            className="flex flex-row bg-white justify-center"
            style={{ minHeight: "550px" }}
          >
            {isImageGenerating ? (
              <Spin size="large" />
            ) : qrImageUrl ? (
              <img
                src={qrImageUrl}
                alt="QR Code"
                style={{ width: 400, height: 550 }}
              />
            ) : (
              <div style={{ opacity: 0, position: "absolute" }}>
                <QRCodeCanvas
                  key={`${qrValue}-${openModal}`}
                  value={qrValue || " "}
                  size={250}
                  level="H"
                  ref={qrCanvasRef}
                />
              </div>
            )}
          </div>
          <Divider />
          <div className="flex justify-end space-x-4">
            <Button
              type="primary"
              onClick={handleDownload}
              style={{
                backgroundColor: "#FFD700",
                color: "#000000",
              }}
              disabled={isImageGenerating || !qrImageUrl}
            >
              Download
            </Button>
            <Button
              type="default"
              onClick={handlePrint}
              disabled={isImageGenerating || !qrImageUrl}
            >
              Print
            </Button>
          </div>
        </div>
      </Modal>
    </ConfigProvider>
  );
}

export default QrModal;
